/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { updateUser } from "../../../api/User";
import { UserAtom } from "../../../atoms/UserAtom";
import { IUserIRoute } from "../../../generated/Api";
import { IFooter } from "../../../types/UserTypes";
import { FormatEmail } from "../../../utils/FormatEmail";
import { nanoidForFooterAndAlias } from "../../NewEmail/Modals/SavedSearch/FooterSearchModal";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import StyledRichInput from "../../StyledRichInput";
import { useModal } from "../Modal";
import { LINE_SEPARATOR_OPTIONS } from "./EmailSettings/container/FooterSettings";

const Body = styled.div`
  position: relative;
  max-height: 80vh;
  border-top: 1px solid #eceeee;
  box-sizing: inherit;
  width: 600px;
  height: 70vh;

  /* @media (max-height: 750px) {
    height: 80vh;
  } */
  margin: 20px -22px 0px;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 68px);
    margin: 0;
    padding: 0 0 96px;

    justify-content: start;
    grid-gap: 32px;

    border-top: none;

    position: relative;
  }
`;

const Content = styled.div`
  overflow-y: auto;
  height: calc(100% - 67px);
  padding-bottom: 20px;

  @media screen and (max-width: 1024px) {
    height: 100%;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin: 30px 22px 0px;
  height: fit-content;
`;

const Title = styled.div`
  color: #333333;
  font-weight: 600;
  display: flex;
  margin-bottom: 13px;
  align-items: center;
`;

const TitleDescription = styled.div`
  color: #33333380;
  font-weight: normal;
  margin-left: 3px;
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eceeee;
  padding: 24px 20px 0px;
  background-color: #fff;

  @media screen and (max-width: 1024px) {
    padding: 24px 20px;

    justify-content: center;
    grid-gap: 10px;

    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;

  @media screen and (max-width: 1024px) {
    button {
      width: fit-content !important;
      padding: 10px !important;
    }
  }
`;

interface Props {
  onSave: (footer: IFooter, footerArray: IFooter[]) => void;
  setAsDefault: (footer: IFooter) => void;
  footer?: any;
}

const EMPTY_FOOTER = {
  id: undefined,
  signature: "",
  name: "",
  additionalFooterText: "",
  textBeforeUnsubscribe: "",
  textAfterUnsubscribe: "",
  hasSeparatorLine: null,
};

export default function CreateOrEditFooter(props: Props): JSX.Element {
  const { onSave, footer } = props;
  const [user, setUser] = useRecoilState(UserAtom);
  const modal = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const [footerOptions, setFooterOptions] = useState({
    id: footer?.id || undefined,
    signature: footer?.signature || "",
    name: footer?.name || "",
    additionalFooterText: footer?.additionalFooterText || "",
    textBeforeUnsubscribe: footer?.textBeforeUnsubscribe || "",
    textAfterUnsubscribe: footer?.textAfterUnsubscribe || "",
    hasSeparatorLine: footer?.hasSeparatorLine || null,
  });

  const {
    signature,
    name,
    additionalFooterText,
    textBeforeUnsubscribe,
    textAfterUnsubscribe,
    hasSeparatorLine,
  } = footerOptions;

  const { footer: footerPreview } = FormatEmail.format(
    "",
    user as IUserIRoute,
    {
      footer: footerOptions,
    }
  );

  async function handleCreateClick() {
    if (!user) return;

    const alreadyExist = user.emailOptions.footers.find(
      (f) =>
        f.name.toLowerCase().trim() === footerOptions.name.toLowerCase().trim()
    );
    if (alreadyExist) {
      toast("Footer with this name already exists", { type: "error" });
      setIsLoading(false);
      return;
    }

    const id = nanoidForFooterAndAlias();

    const newFooterArray = [
      ...user.emailOptions?.footers!,
      { ...footerOptions, id },
    ];

    const isSuccessUpdateUser = await updateUser({
      emailOptions: {
        ...user.emailOptions,
        footers: newFooterArray,
      },
    });

    if (isSuccessUpdateUser) {
      setUser((u) => ({
        ...u,
        emailOptions: {
          ...u?.emailOptions,
          footers: newFooterArray,
        },
      }));

      onSave({ ...footerOptions, id }, newFooterArray);

      toast("Footer created", { type: "success" });
      modal.popModal();
    }

    setIsLoading(false);
  }

  async function handleSetAsDefault() {
    const defaultFooter = {
      ...user.emailOptions.footers[0],
      additionalFooterText: footerOptions.additionalFooterText,
      signature: footerOptions.signature,
      textBeforeUnsubscribe: footerOptions.textBeforeUnsubscribe,
      textAfterUnsubscribe: footerOptions.textAfterUnsubscribe,
      hasSeparatorLine: footerOptions.hasSeparatorLine,
    };

    let footers = [...user.emailOptions.footers];
    footers[0] = defaultFooter;

    const isSuccessUpdateUser = await updateUser({
      emailOptions: {
        ...user.emailOptions,
        footers,
      },
    });

    if (isSuccessUpdateUser) {
      setUser((u) => ({
        ...u,
        emailOptions: { ...user.emailOptions, footers: footers },
      }));

      props.setAsDefault(defaultFooter);

      toast("Default footer updated", { type: "success" });
    }
  }

  async function handleEditClick() {
    if (!user) return;

    const alreadyExist = user.emailOptions.footers.find(
      (f) =>
        f.name === footerOptions.name.toLowerCase().trim() &&
        f.id !== footerOptions.id.toLowerCase().trim()
    );

    if (alreadyExist) {
      toast("Footer with this name already exists", { type: "error" });
      setIsLoading(false);
      return;
    }

    const newFooterArray = user.emailOptions.footers.map((f) => {
      if (f.id !== footerOptions.id) return f;
      return footerOptions;
    });

    const isSuccessUpdateUser = await updateUser({
      emailOptions: {
        ...user.emailOptions,
        footers: newFooterArray,
      },
    });

    if (isSuccessUpdateUser) {
      setUser((u) => ({
        ...u,
        emailOptions: { ...user.emailOptions, footers: newFooterArray },
      }));

      onSave(footerOptions, newFooterArray);

      toast("Footer updated", { type: "success" });
      modal.popModal();
    }
    setIsLoading(false);
  }

  return (
    <Body>
      <Content>
        <FlexColumn
          css={css`
            margin-top: 20px;
          `}
        >
          <Title>Footer Name</Title>
          <StyledInput
            value={name}
            disabled={user.emailOptions.footers[0]?.id === footer?.id}
            onChange={(evt) =>
              setFooterOptions((f) => ({ ...f, name: evt.target.value }))
            }
            placeholder="Enter footer name"
          />
        </FlexColumn>
        <FlexColumn>
          <Title>
            Email Signature <TitleDescription>(Optional)</TitleDescription>
          </Title>

          <StyledRichInput
            placeholder="Enter email signature"
            value={signature}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                signature: value.replace("<p>", "").replace("</p>", ""),
              }))
            }
          />
        </FlexColumn>
        <FlexColumn>
          <Title>
            Additional footer text
            <TitleDescription>(Optional)</TitleDescription>
          </Title>

          <StyledRichInput
            placeholder="Enter additional footer text"
            value={additionalFooterText}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                additionalFooterText: value
                  .replace("<p>", "")
                  .replace("</p>", ""),
              }))
            }
          />
        </FlexColumn>
        <FlexColumn>
          <Title>
            Text before Unsubscribe link
            <TitleDescription>(Optional)</TitleDescription>
          </Title>

          <StyledRichInput
            placeholder="Enter text before Unsubscribe link"
            value={textBeforeUnsubscribe}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                textBeforeUnsubscribe: value
                  .replace("<p>", "")
                  .replace("</p>", ""),
              }))
            }
          />
        </FlexColumn>
        <FlexColumn>
          <Title>
            Text after Unsubscribe link
            <TitleDescription>(Optional)</TitleDescription>
          </Title>

          <StyledRichInput
            placeholder="Enter text after Unsubscribe link"
            value={textAfterUnsubscribe}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                textAfterUnsubscribe: value
                  .replace("<p>", "")
                  .replace("</p>", ""),
              }))
            }
          />
        </FlexColumn>
        <FlexColumn>
          <Title>Line Separator</Title>
          <StyledAutocompleteSelect
            options={LINE_SEPARATOR_OPTIONS}
            selectedOption={LINE_SEPARATOR_OPTIONS.find(
              (l) => l.value === hasSeparatorLine
            )}
            onOptionSelect={(vle) => {
              if (vle.value === "") return;
              setFooterOptions((f) => ({
                ...f,
                hasSeparatorLine: vle.value as any,
              }));
            }}
            placeholder="Type or select an option"
          />
        </FlexColumn>

        <FlexColumn>
          <Title>Preview</Title>

          <div
            css={css`
              /* word-wrap: break-word; */
              white-space: pre-wrap;
              background-color: #00000008;
              border-radius: 5px;
              padding: 14px;
            `}
            dangerouslySetInnerHTML={{ __html: footerPreview }}
          />
        </FlexColumn>
      </Content>
      <Bottom>
        <Buttons>
          <StyledButton
            inverted
            onClick={() => setFooterOptions(EMPTY_FOOTER)}
            style={{ marginRight: "10px", width: "92px" }}
          >
            Reset
          </StyledButton>

          <StyledButton
            inverted
            disable={footerOptions.id === user.emailOptions.footers[0].id}
            onClick={handleSetAsDefault}
            style={{ width: "140px" }}
          >
            Set as default
          </StyledButton>
        </Buttons>
        <Buttons>
          <StyledButton
            inverted
            onClick={() => modal.popModal()}
            style={{ marginRight: "10px", width: "92px" }}
          >
            Cancel
          </StyledButton>
          <StyledButton
            style={{ width: "92px" }}
            isLoading={isLoading}
            onClick={() => {
              if (isLoading) return;

              setIsLoading(true);

              footer ? handleEditClick() : handleCreateClick();
            }}
          >
            Save
          </StyledButton>
        </Buttons>
      </Bottom>
    </Body>
  );
}
